import * as React from 'react';
import type { ParentStudyViewModel } from '../../../Semester/ParentStudyViewModel';
import type { OnlineStudySubject } from '../../../ViewModel/Subjects/OnlineStudySubject.csharp';
import { OnlineStudyPlanContext } from '../../Context/OnlineStudyPlanContext';
import { ChoiceEqualsChoice, IsChoiceAddedToBasket } from '../../Context/OnlineStudyPlanHelpers';
import type { OnlineSemesterItemViewModel } from '../SubjectsBySemester';
import { useMessageHandler } from '~/Features/Messaging/useMessageHandler';
import type { IParentItem } from '~/Features/OnlineApplication/Model';
import { RemovedFromBasket } from '~/Features/Pages/OnlineStudyPage/Subjects/Basket/Events/SubjectsBasketEvents';

interface ElectiveItemModelProps {
    parentStudy: ParentStudyViewModel;
    parentItem: IParentItem;
    item: OnlineSemesterItemViewModel;
    setItemState: (
        item: OnlineSemesterItemViewModel,
        setter: (prevItemState: OnlineSemesterItemViewModel) => void
    ) => void;
}

export const useElectiveItemModel = ({ parentStudy, parentItem, item, setItemState }: ElectiveItemModelProps) => {
    const { choicesFromBasket } = React.useContext(OnlineStudyPlanContext);

    useMessageHandler(RemovedFromBasket.id, (event: RemovedFromBasket) => {
        setItemState(item, currentItem => {
            currentItem.selectedElectiveSubjects = [...currentItem.selectedElectiveSubjects].filter(subject =>
                event.choices.every(c => ChoiceEqualsChoice(c, { subject, parentStudy, parentItem }) === false)
            );
        });
    });

    const toggleElectiveSubjectsDropdown = () => {
        setItemState(item, currentItem => {
            currentItem.hasOpenedAvailableElectiveSubjects = !currentItem.hasOpenedAvailableElectiveSubjects;
        });
    };

    const addSelectedElectiveSubject = (subject: OnlineStudySubject) => {
        setItemState(item, currentItem => {
            currentItem.selectedElectiveSubjects = [...currentItem.selectedElectiveSubjects, subject];
        });
    };

    const removeSelectedElectiveSubject = (subject: OnlineStudySubject) => {
        setItemState(item, currentItem => {
            currentItem.selectedElectiveSubjects = [...currentItem.selectedElectiveSubjects].filter(
                s => s.code !== subject.code
            );
        });
    };

    const getItemElectiveSubjectsAddedToBasket = (): OnlineStudySubject[] =>
        item.selectedElectiveSubjects.filter(subject =>
            IsChoiceAddedToBasket(choicesFromBasket, {
                subject,
                parentStudy,
                parentItem,
            })
        );

    return {
        item,
        toggleElectiveSubjectsDropdown,
        addSelectedElectiveSubject,
        removeSelectedElectiveSubject,
        getItemElectiveSubjectsAddedToBasket,
    };
};
